import { useState, useEffect } from 'react';
import api, { todayStr, dateToStr, WEEKDAY_LABELS, WEEKDAY_SHORT, SOURCE_COLOR, STATUS_COLOR, STATUS_LABELS } from '../lib/api';
import { ChevronLeft, ChevronRight, Users, Search, X as XIcon } from 'lucide-react';
import { ReservationDialog } from '../components/ReservationDialogs';

function getMonday(d) {
  const date = new Date(d);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(date.setDate(diff));
}

export default function AdminWeek() {
  const [monday, setMonday] = useState(() => getMonday(new Date()));
  const [reservations, setReservations] = useState([]);
  const [tables, setTables] = useState([]);
  const [areas, setAreas] = useState([]);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');

  const matchResv = (r, q) => {
    if (!q) return true;
    const s = q.trim().toLowerCase();
    return (r.customer_name || '').toLowerCase().includes(s)
      || (r.phone || '').toLowerCase().includes(s)
      || (r.email || '').toLowerCase().includes(s);
  };

  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(d.getDate() + i);
    return d;
  });

  const dateFrom = dateToStr(days[0]);
  const dateTo = dateToStr(days[6]);

  const load = async () => {
    const [r, t, a] = await Promise.all([
      api.get(`/reservations?date_from=${dateFrom}&date_to=${dateTo}`),
      api.get('/tables'),
      api.get('/areas'),
    ]);
    setReservations(r.data.filter(x => x.status !== 'completed'));
    setTables(t.data);
    setAreas(a.data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [dateFrom, dateTo]);

  const prev = () => {
    const d = new Date(monday);
    d.setDate(d.getDate() - 7);
    setMonday(d);
  };
  const next = () => {
    const d = new Date(monday);
    d.setDate(d.getDate() + 7);
    setMonday(d);
  };
  const today = () => setMonday(getMonday(new Date()));

  return (
    <div className="p-4 md:p-8 max-w-7xl" data-testid="admin-week-page">
      <header className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#5C5C59]">Wochenübersicht</p>
          <h1 className="font-display text-3xl text-[#1C1C19]" data-testid="week-range-label">
            {days[0].toLocaleDateString('de-DE', { day: '2-digit', month: 'short' })} – {days[6].toLocaleDateString('de-DE', { day: '2-digit', month: 'short', year: 'numeric' })}
          </h1>
        </div>
        <div className="flex gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2 top-2.5 text-[#5C5C59]" />
            <input
              type="text"
              placeholder="Gast suchen..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              data-testid="week-search-input"
              className="w-40 sm:w-56 pl-8 pr-7 py-2 border border-[#E5E5E0] rounded text-sm focus:outline-none focus:border-[#1C1C19]"
            />
            {search && (
              <button onClick={() => setSearch('')} data-testid="week-search-clear" className="absolute right-2 top-2.5 text-[#5C5C59] hover:text-[#1C1C19]">
                <XIcon className="w-4 h-4" />
              </button>
            )}
          </div>
          <button onClick={prev} data-testid="week-prev" className="p-2 border border-[#E5E5E0] rounded hover:border-[#1C1C19]"><ChevronLeft className="w-4 h-4" /></button>
          <button onClick={today} data-testid="week-today" className="px-4 py-2 border border-[#E5E5E0] rounded text-sm hover:border-[#1C1C19]">Heute</button>
          <button onClick={next} data-testid="week-next" className="p-2 border border-[#E5E5E0] rounded hover:border-[#1C1C19]"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-7 gap-3">
        {days.map((d, i) => {
          const ds = dateToStr(d);
          const dayResvs = reservations.filter(r => r.date === ds && matchResv(r, search)).sort((a, b) => a.start_time.localeCompare(b.start_time));
          const totalPersons = dayResvs.filter(r => !['cancelled', 'declined', 'no_show'].includes(r.status)).reduce((s, r) => s + r.persons, 0);
          const isToday = ds === todayStr();
          return (
            <div key={ds} className={`bg-white border rounded-lg overflow-hidden ${isToday ? 'border-[#1C1C19]' : 'border-[#E5E5E0]'}`} data-testid={`week-day-${ds}`}>
              <div className={`px-3 py-2 border-b ${isToday ? 'bg-[#1C1C19] text-white border-[#1C1C19]' : 'border-[#E5E5E0]'}`}>
                <div className="flex items-baseline justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-wider opacity-80">{WEEKDAY_SHORT[i]}</div>
                    <div className="font-display text-xl">{d.getDate()}</div>
                  </div>
                  <div className="text-xs flex items-center gap-1 opacity-80">
                    <Users className="w-3 h-3" /> {totalPersons}
                  </div>
                </div>
              </div>
              <div className="divide-y divide-[#E5E5E0] max-h-96 overflow-y-auto scrollbar-thin">
                {dayResvs.length === 0 ? (
                  <div className="p-3 text-xs text-[#9CA3AF] text-center">—</div>
                ) : dayResvs.map(r => (
                  <button
                    key={r.id}
                    onClick={() => setSelected(r)}
                    data-testid={`week-resv-${r.id}`}
                    className="w-full text-left p-2 hover:bg-[#F9F9F7] transition"
                  >
                    <div className="flex items-center gap-2">
                      <span className="w-1 h-6 rounded" style={{ background: SOURCE_COLOR[r.source] }}></span>
                      <div className="min-w-0 flex-1">
                        <div className="font-mono text-xs text-[#1C1C19]">{r.start_time}</div>
                        <div className="text-xs text-[#1C1C19] truncate font-medium">{r.customer_name}</div>
                        <div className="text-xs text-[#5C5C59]">{r.persons} P.</div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <ReservationDialog
        open={!!selected}
        onClose={() => setSelected(null)}
        reservation={selected}
        tables={tables}
        areas={areas}
        onUpdate={load}
      />
    </div>
  );
}
